import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const InformeSecretaria = () => {
  const { user } = useAuth();
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [año, setAño] = useState(new Date().getFullYear());
  const [estadisticas, setEstadisticas] = useState(null);
  const [reuniones, setReuniones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    cargarInforme();
  }, [mes, año]);

  const cargarInforme = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/puntos-reunion/informe/mensual', {
        params: { mes, año }
      });
      setEstadisticas(response.data.estadisticas);
      setReuniones(response.data.reuniones);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error al cargar el informe: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const getNombreMes = (mesNum) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mesNum - 1];
  };

  const getTipoReunionLabel = (tipo) => {
    const tipos = {
      'reunion_ordinaria': 'Reunión Ordinaria',
      'asamblea': 'Asamblea',
      'junta_directiva': 'Junta Directiva',
      'reunion_extraordinaria': 'Reunión Extraordinaria'
    };
    return tipos[tipo] || tipo;
  };

  const getBadgeColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success';
      case 'pendiente_revision': return 'warning';
      case 'borrador': return 'secondary';
      case 'rechazado': return 'danger';
      default: return 'info';
    }
  };

  const descargarPDF = () => {
    // Placeholder para descarga de PDF
    alert('Funcionalidad de descarga PDF próximamente');
  };

  if (!estadisticas && !loading) {
    return <Container className="mt-4"><Alert variant="info">Cargando informe...</Alert></Container>;
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2><i className="fas fa-file-alt me-2"></i>Informe Mensual de Secretaría</h2>
          <p className="text-muted">Período: {getNombreMes(mes)} de {año}</p>
        </Col>
        <Col md={2} className="text-end">
          <Button variant="primary" onClick={descargarPDF}>
            <i className="fas fa-download me-2"></i>Descargar PDF
          </Button>
        </Col>
      </Row>

      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
          {message.text}
        </Alert>
      )}

      {/* Filtros */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Mes</Form.Label>
            <Form.Select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                <option key={m} value={m}>{getNombreMes(m)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Año</Form.Label>
            <Form.Select value={año} onChange={(e) => setAño(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027, 2028].map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center"><p>Cargando informe...</p></div>
      ) : estadisticas ? (
        <>
          {/* Tarjetas de Estadísticas */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-calendar-check fa-3x text-primary mb-3"></i>
                  <h5 className="text-primary">Reuniones Realizadas</h5>
                  <h3 className="text-primary">{estadisticas.totalReuniones}</h3>
                  <small className="text-muted">
                    {estadisticas.reunionesAprobadas} aprobadas
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-list fa-3x text-success mb-3"></i>
                  <h5 className="text-success">Puntos Tratados</h5>
                  <h3 className="text-success">{estadisticas.totalPuntos}</h3>
                  <small className="text-muted">
                    {estadisticas.puntosCompletados} completados
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-users fa-3x text-info mb-3"></i>
                  <h5 className="text-info">Asistencia Promedio</h5>
                  <h3 className="text-info">{estadisticas.asistenciaPromedio}%</h3>
                  <ProgressBar 
                    now={estadisticas.asistenciaPromedio} 
                    className="mt-2"
                    variant={estadisticas.asistenciaPromedio > 80 ? 'success' : estadisticas.asistenciaPromedio > 60 ? 'warning' : 'danger'}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-hourglass-half fa-3x text-warning mb-3"></i>
                  <h5 className="text-warning">Pendientes</h5>
                  <h3 className="text-warning">{estadisticas.puntosPendientes}</h3>
                  <small className="text-muted">
                    Puntos sin completar
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Resumen de Actas */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header className="bg-dark text-white">
                  <h6 className="mb-0">Resumen de Actas de Reunión</h6>
                </Card.Header>
                <Card.Body>
                  {reuniones.length === 0 ? (
                    <Alert variant="info">No hay actas registradas para este período</Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Fecha</th>
                            <th>Tipo de Reunión</th>
                            <th>Lugar</th>
                            <th>Puntos</th>
                            <th>Completados</th>
                            <th>Asistentes</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reuniones.map(reunion => {
                            const puntosCompletados = reunion.puntos.filter(p => p.estado === 'completado').length;
                            const asistentesPres = reunion.asistentes.filter(a => a.presente).length;
                            return (
                              <tr key={reunion._id}>
                                <td>{new Date(reunion.fechaReunion).toLocaleDateString()}</td>
                                <td>{getTipoReunionLabel(reunion.tipoReunion)}</td>
                                <td>{reunion.lugarReunion || '-'}</td>
                                <td className="text-center">
                                  <Badge bg="info">{reunion.puntos.length}</Badge>
                                </td>
                                <td className="text-center">
                                  <Badge bg={puntosCompletados === reunion.puntos.length ? 'success' : 'warning'}>
                                    {puntosCompletados}/{reunion.puntos.length}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <span>{asistentesPres}/{reunion.asistentes.length}</span>
                                </td>
                                <td>
                                  <Badge bg={getBadgeColor(reunion.estado_aprobacion)}>
                                    {reunion.estado_aprobacion}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Detalles de Puntos Pendientes */}
          {estadisticas.puntosPendientes > 0 && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Header className="bg-warning text-dark">
                    <h6 className="mb-0">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      Puntos Pendientes ({estadisticas.puntosPendientes})
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table striped bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Fecha Reunión</th>
                            <th>Punto</th>
                            <th>Descripción</th>
                            <th>Responsable</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reuniones.flatMap(reunion =>
                            reunion.puntos
                              .filter(p => p.estado !== 'completado')
                              .map((punto, idx) => (
                                <tr key={`${reunion._id}-${idx}`}>
                                  <td>{new Date(reunion.fechaReunion).toLocaleDateString()}</td>
                                  <td><strong>{punto.numero}. {punto.tema}</strong></td>
                                  <td>{punto.descripcion}</td>
                                  <td>{punto.responsable || '-'}</td>
                                  <td>
                                    <Badge bg={punto.estado === 'pendiente' ? 'danger' : punto.estado === 'en_progreso' ? 'warning' : 'secondary'}>
                                      {punto.estado.replace(/_/g, ' ')}
                                    </Badge>
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Conclusiones */}
          <Row>
            <Col>
              <Card>
                <Card.Header className="bg-secondary text-white">
                  <h6 className="mb-0">Conclusiones y Recomendaciones</h6>
                </Card.Header>
                <Card.Body>
                  <p>
                    <strong>Resumen del período:</strong> En el mes de {getNombreMes(mes)} de {año} se realizaron 
                    <strong> {estadisticas.totalReuniones} reuniones</strong> con un total de <strong>{estadisticas.totalPuntos} puntos</strong> a tratar.
                  </p>
                  <p>
                    Se logró completar <strong>{estadisticas.puntosCompletados}</strong> puntos ({((estadisticas.puntosCompletados / estadisticas.totalPuntos) * 100).toFixed(1)}%).
                    Aún quedan <strong>{estadisticas.puntosPendientes}</strong> puntos pendientes de completar.
                  </p>
                  <p>
                    La asistencia promedio fue de <strong>{estadisticas.asistenciaPromedio}%</strong>, lo cual indica una {estadisticas.asistenciaPromedio > 80 ? 'excelente' : estadisticas.asistenciaPromedio > 60 ? 'buena' : 'regular'} participación.
                  </p>
                  <p className="mt-3 text-muted">
                    <em>Generado: {new Date().toLocaleString()}</em>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}
    </Container>
  );
};

export default InformeSecretaria;
